<script lang="ts">

import { Component, Prop, Vue } from "vue-facing-decorator";
import FilterComponent from "@/components/search/Filter.vue";
import IconComponent from "@/components/atoms/Icon.vue";
import SearchInputComponent from "@/components/molecules/SearchInput.vue";
import { useAppliedFiltersStore } from "@/domain/search/application/AppliedFilters.store";
import { useSearchResultsStore } from "@/domain/search/application/SearchResults.store";
import { IconName, LanguageCode, SearchQASelectors, TranslationKey, TranslationService } from "webshop-modules";
import { SearchAppData } from "@/domain/search/entitites/TranslationsData.interface";

@Component({
    components: {
        FilterComponent,
        IconComponent,
        SearchInputComponent
    }
})
export default class SearchComponent extends Vue {

    @Prop()
    public data!: SearchAppData;

    public readonly appliedFiltersStore = useAppliedFiltersStore();
    public readonly searchResultsStore = useSearchResultsStore();

    public get numOfEvents(): number {
        return this.searchResultsStore.resultList.totalCount !== undefined
            ? this.searchResultsStore.resultList.totalCount
            : 0;
    }

    public get numOfArtists(): number | undefined {
        return this.searchResultsStore.artistsList.totalCount;
    }

    public readonly IconName: typeof IconName = IconName;

    public readonly Translation: typeof TranslationKey = TranslationKey;
    public readonly SearchQASelectors: typeof SearchQASelectors = SearchQASelectors;

    public lang: LanguageCode = TranslationService.getLanguageCode();

    public searchLabel = '';
    public citiesLabel = '';

    get filtersActive(): boolean {
        return Object.values(this.appliedFiltersStore.$state).flat().length > 0;
    }

    get showActiveFilters(): boolean {
        if (!window.matchMedia('(min-width: 960px)').matches) {
            return false;
        } else {
            return this.filtersActive;
        }
    }

    get isDesktop(): boolean {
        return window.matchMedia('(min-width: 960px)').matches;
    }

    public getFilterName(category: string, id: string) {
        const langCode = this.lang === LanguageCode.English
            ? LanguageCode.English
            : 'lc';
        switch (category) {
            case 'country':
                return this.data.countryTranslations[langCode][id.toUpperCase()];
            case 'category':
                return this.data.categoryTranslations.category[langCode][id] !== undefined
                    ? this.data.categoryTranslations.category[langCode][id]
                    : this.data.categoryTranslations.mainCategory[langCode][5];
            case 'mainCategory':
                return this.data.categoryTranslations.mainCategory[langCode][id] !== undefined
                    ? this.data.categoryTranslations.mainCategory[langCode][id]
                    : this.data.categoryTranslations.mainCategory[langCode][5];
            default:
                return id;
        }
    }

    public formatDateForDisplay(date: Date | undefined | null) {

        if (date === undefined || date === null) {
            return '';
        }

        let formattedDate: Date = new Date(date);

        return formattedDate.toLocaleDateString(TranslationService.getLanguageCode(), {
            year: 'numeric', month: 'numeric', day: 'numeric'
        });
    }

    public async clearFilter(category: string, filter: string) {
        await this.appliedFiltersStore.deleteAppliedFilters({[category]: filter});
        this.searchResultsStore.getData(false, this.$apiService);
    }

    public async clearDates() {
        await this.appliedFiltersStore.deleteAppliedFilters({dateStart: undefined, dateEnd: undefined});
        this.searchResultsStore.getData(false, this.$apiService);
    }

    private async setComponentTranslations() {
        this.searchLabel = await this.$translationService
            .getTranslationFor(TranslationKey.Search_label);
        this.citiesLabel = await this.$translationService
            .getTranslationFor(TranslationKey.Filter_city);
    }

    private created() {
        this.setComponentTranslations();
    }
}
</script>

<template>
    <div id="search-component" class="o-searchComponent" :data-qa="SearchQASelectors.SEARCH_INPUT_COMPONENT">
        <div class="l-container-pageheader l-container l-bg-primary">
            <div class="m-pageHeader">
                <div class="m-pageHeader__rimL"></div>
                <div class="m-pageHeader__container">
                    <div class="m-pageHeader__row">
                        <form @submit.prevent>
                            <SearchInputComponent
                                :label="searchLabel"
                                :name="'api-search'"
                                :icon="IconName.Search"
                            />
                        </form>

                        <FilterComponent :data="data" :cities-label="citiesLabel"/>
                    </div>

                    <div class="m-pageHeader__row" v-if="isDesktop">
                        <div class="m-pageHeader__filters">
                            <template v-if="showActiveFilters">
                                <template v-for="(list,c) in appliedFiltersStore.$state">
                                    <template v-if="typeof list !== 'string' && c !== 'offset'">
                                        <template v-for="f in list">
                                            <button
                                                class="a-btnSmall -primary -filtered"
                                                @click="clearFilter(c,f)"
                                            >
                                                <icon-component :icon-id="IconName.Cross" class="a-btnIcon"/>
                                                <span>{{ getFilterName(c, f) }}</span>
                                            </button>
                                        </template>
                                    </template>
                                </template>
                            </template>
                            <template v-if="appliedFiltersStore.$state.dateStart || appliedFiltersStore.$state.dateEnd">
                                <button class="a-btnSmall -primary -filtered" @click="clearDates">
                                    <icon-component :icon-id="IconName.Cross" class="a-btnIcon"/>
                                    <span>{{formatDateForDisplay(appliedFiltersStore.$state.dateStart as Date) }}
                                        - {{ formatDateForDisplay(appliedFiltersStore.$state.dateEnd as Date) }}</span>
                                </button>
                            </template>
                        </div>
                        <div class="m-pageHeader__count">
                            <span v-translate="{ key: this.Translation.Navigation_section_events }">Events</span>:
                            &nbsp;
                            {{ numOfEvents }}
                        </div>
                    </div>
                </div>
                <div class="m-pageHeader__rimR"></div>
            </div>
        </div>
    </div>
</template>
